import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import QRCodeStyling from 'qr-code-styling';
import jsPDF from 'jspdf';
import { IActivo } from 'modelos/src';

import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';

interface IQrVehiculoData {
  activo: IActivo;
}

@Component({
  selector: 'app-qr-vehiculo',
  templateUrl: './qr-vehiculo.component.html',
  styleUrls: ['./qr-vehiculo.component.scss'],
  standalone: true,
  imports: [AuxiliaresModule],
})
export class QrVehiculoComponent implements AfterViewInit {
  data = inject<IQrVehiculoData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<QrVehiculoComponent>>(MatDialogRef);
  private helper = inject(HelperService);

  @ViewChild('qrContainer', { static: true })
  qrContainer!: ElementRef<HTMLDivElement>;

  public patenteRaw = '';
  public patenteFormat = '';
  public loading = false;
  public tamanioCm = 13;
  public readonly minCm = 3;
  public readonly maxCm = 18;

  private qr?: QRCodeStyling;

  async ngAfterViewInit(): Promise<void> {
    this.patenteRaw = (this.data.activo.vehiculo?.patente || '')
      .replace(/\s+/g, '')
      .toUpperCase();
    this.patenteFormat = this.helper.formatPatente(this.patenteRaw);
    await this.renderQr();
  }

  private async renderQr(): Promise<void> {
    const logoImage = await this.loadImageAsDataUrl(
      'assets/images/irix_logo.png',
    );
    this.qr = new QRCodeStyling({
      width: 320,
      height: 320,
      type: 'canvas',
      shape: 'square',
      data: this.patenteRaw,
      image: logoImage,
      margin: 8,
      qrOptions: { errorCorrectionLevel: 'H' },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.5,
        margin: 4,
      },
      dotsOptions: { type: 'square', color: '#000000' },
      cornersSquareOptions: { type: 'square', color: '#000000' },
      cornersDotOptions: { type: 'square', color: '#000000' },
      backgroundOptions: { color: '#ffffff' },
    });
    this.qrContainer.nativeElement.innerHTML = '';
    this.qr.append(this.qrContainer.nativeElement);
  }

  public async descargarPdf(): Promise<void> {
    if (!this.qr) return;
    this.loading = true;
    try {
      const raw = await this.qr.getRawData('png');
      if (!raw) throw new Error('No se pudo generar el QR');
      const qrDataUrl = await this.blobToDataUrl(raw as Blob);

      const doc = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const cm = Math.min(
        this.maxCm,
        Math.max(this.minCm, this.tamanioCm || 13),
      );
      const qrSize = cm * 10;
      const patenteFontSize = Math.max(20, Math.min(60, qrSize * 0.3));
      const patenteGap = Math.max(15, qrSize * 0.15);
      const patenteAreaHeight = patenteGap + patenteFontSize * 0.4;
      const qrX = (pageWidth - qrSize) / 2;
      const qrY = Math.max(20, (pageHeight - qrSize - patenteAreaHeight) / 2);
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(patenteFontSize);
      doc.text(this.patenteFormat, pageWidth / 2, qrY + qrSize + patenteGap, {
        align: 'center',
      });

      const fileName = `qr-${this.patenteRaw || 'vehiculo'}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private loadImageAsDataUrl(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo crear canvas para el logo'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}
